import cloneDeep from 'lodash/cloneDeep';

import moment from 'moment';
import qs from 'qs';
import _reduce from 'lodash/reduce';
import _map from 'lodash/map';

import { handleActions } from 'redux-actions';
import { getFormValues } from 'redux-form';
import axios from 'axios';
import endpoints from '../../config/endpoints';
import intl from '../shared/intl';
import createFilters from '../shared/filters';
import checkPermissions from '../shared/check-permissions';
import { default as createCalendar, getDateRangeFromCalendar } from '../shared/calendar';
import { actions as rootActions } from '../root';
import { createSelectOptionsFromSysEnums } from '../shared/form-elements/select';
import createDatePicker from '../shared/date-picker';

const calendar = createCalendar({
	namespace: 'finReports',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment()
			.subtract('1', 'month')
			.format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const datepicker = createDatePicker({
	namespace: 'finReports',
	stateNodeName: 'datepicker',
	defaultValue: {
		start: moment()
			.format('YYYY-MM-DD')
	}
});

const accrualReportCalendar = createCalendar({
	namespace: 'finReports',
	stateNodeName: 'accrualReportCalendar',
	defaultValue: {
		start: moment()
			.subtract('1', 'week')
			.format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'finReports',
	stateNodeName: 'filters',
	listOfFilters: [{
		name: 'offers',
		title: 'Offers',
		placeholder: 'Add offers',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.OFFERS
		}
	}, {
		name: 'affiliates',
		title: 'Affiliates',
		placeholder: 'Add affiliates',
		value: [],
		multi: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATES
		}
	}, {
		name: 'countries',
		title: 'Countries',
		placeholder: 'Add countries',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.LOCATIONS
		}
	}, {
		name: 'is_conversion_transactions',
		title: 'Is conversion transactions',
		placeholder: 'Only conversion transactions',
		value: [],
		multi: false,
		options: {
			source: [{
				value: true,
				label: 'Is conversion transactions'
			}, {
				value: false,
				label: 'Is without conversion transactions'
			}],
			type: 'provided'
		}
	}]
});

// actions types
const FETCH_DATA_SUCCESS = 'fin-reports/fetch-data-success';
const TOGGLE_PROGRESS_BAR = 'fin-reports/toggle-progress-bar';

const GET_BALANCES_SUCCESS = 'fin-reports/get-balances-success';
const GET_BALANCES_ERROR = 'fin-reports/get-balances-error';

// permissions
export const allowedPermissions = {
	view: 'fin_reports_view',
	manualAdjustmentsView: 'manual_adjustments_view'
};

// initial state
const initialState = {
	data: null,
	calendar: calendar.initialState,
	accrualReportCalendar: accrualReportCalendar.initialState,
	datepicker: datepicker.initialState,
	showProgressBar: false,
	filters: filters.initialState,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => dispatch => {
			setTimeout(() => dispatch({ type: FETCH_DATA_SUCCESS }), 1);
		},

		requestDeferredStatistics: (reportName) => (dispatch, getState) => {
			const email = getState().root.data.email;

			confirm(
				`Your request failed to process in 30 seconds. Request deferred report to be sent to email ${email}?`
			) &&
				dispatch(actions.getReport(reportName, true));
		},

		getReport: (reportName, deferred = false) => (dispatch, getState) => {
			const currencies = selectors.getCurrentCurrencies(getState());
			const { calendar } = getState().finReports;
			const date_range = getDateRangeFromCalendar(calendar);
			let url = `${endpoints.FIN_REPORTS.BASE}/${reportName}?${qs.stringify({ date_range, currencies })}`;
			if (reportName === 'manual-adjustments') {
				url = `${endpoints.FIN_REPORTS.MANUAL_ADJUSTMENTS}?${qs.stringify({ date_range, currencies })}`;
			}
			if (reportName === 'increase-approve') {
				url = `${endpoints.FIN_REPORTS.INCREASE_APPROVE}?${qs.stringify({ date_range, currencies })}`;
			}
			dispatch(rootActions.spinnerToggle());

			if (deferred) {
				url += '&is_deferred=1';
			}

			return axios({
				url,
				method: 'GET',
				responseType: 'blob'
			}).then((response) => {
				dispatch(rootActions.spinnerToggle());

				if (deferred) {
					return;
				}

				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement('a');
				const startDate = moment(date_range.start).format('DD.MM.YYYY');
				const endDate = moment(date_range.end).format('DD.MM.YYYY');
				let filename;
				const currenciesNamePart = currencies && currencies.length ? `(${currencies.join(',')})` : '';
				if (reportName === 'balances-movement') {
					filename = `ОСВ ${currenciesNamePart} за период с ${startDate} по ${endDate}.csv`;
				} else if (reportName === 'conversions') {
					filename = `Конверсии ${currenciesNamePart} за период с ${startDate} по ${endDate}.csv`;
				} else if (reportName === 'manual-adjustments') {
					filename = `Ручные корректировки ${currenciesNamePart} за период с ${startDate} по ${endDate}.csv`;
				} else if (reportName === 'increase-approve') {
					filename = `Гаранты за период с ${startDate} по ${endDate}.csv`;
				}

				link.href = url;
				link.setAttribute('download', filename);
				document.body.appendChild(link);
				link.click();
			})
				.catch(() => {
					dispatch(rootActions.spinnerToggle());
					return dispatch(actions.requestDeferredStatistics(reportName));
				});
		},

		getFinancialReports: (type = '') => (dispatch, getState) => {

			const { accrualReportCalendar } = getState().finReports;
			const currencies = selectors.getCurrentCurrencies(getState());

			const filters = _reduce(
				selectors.getFilters(getState()).selectedFilters,
				(aggr, i) => ({...aggr, [i.name]: i.value.map(val => val.value || val.id)}),
				{}
			);

			dispatch(rootActions.spinnerToggle());
			const date_range = getDateRangeFromCalendar(accrualReportCalendar);
			let url = `${endpoints.FIN_REPORTS.ACCRUALS}?${qs.stringify({ date_range, filters, currencies })}`;

			const startDate = moment(date_range.start).format('DD.MM.YYYY');
			const endDate = moment(date_range.end).format('DD.MM.YYYY');

			if (type === 'payout-reports') {
				url = `${endpoints.FIN_REPORTS.PAYOUT_REPORT}?${qs.stringify({ date_range, filters, currencies })}`;
			}

			return axios({
				url,
				method: 'GET',
				responseType: 'blob'
			}).then((response) => {
				dispatch(rootActions.spinnerToggle());

				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement('a');
				let filename = `Начисления в период с ${startDate} по ${endDate}.csv`;
				if (type === 'payout-reports') {
					filename = `Выплаты в период с ${startDate} по ${endDate}.csv`;
				}
				link.href = url;
				link.setAttribute('download', filename);
				document.body.appendChild(link);
				link.click();
			})
				.catch(() => {
					dispatch(rootActions.spinnerToggle());
				});
		},

		getBalanceReport: () => (dispatch, getState) => {
			const { datepicker } = getState().finReports;
			let url = `${endpoints.FIN_REPORTS.BALANCES_REPORT}?${qs.stringify({
				start: datepicker.value.start || datepicker.value
			})}`;

			dispatch(rootActions.spinnerToggle());

			return axios({
				url,
				method: 'GET',
				responseType: 'blob'
			}).then((response) => {
				dispatch(rootActions.spinnerToggle());

				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement('a');
				let filename = `Балансы в начале дня по UTC ${datepicker.value.start || datepicker.value}.csv`;
				link.href = url;
				link.setAttribute('download', filename);
				document.body.appendChild(link);
				link.click();
			})
				.catch(() => {
					dispatch(rootActions.spinnerToggle());
				});
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		})
	},
	calendar.actions,
	datepicker.actions,
	accrualReportCalendar.actions,
	filters.actions
);

// reducers
const reducers = Object.assign(
	{
		// placeholder data needed because of state machine and init func = fetchData
		[FETCH_DATA_SUCCESS]: (state, action) => ({ ...state, data: 'placeholder' }),
		[GET_BALANCES_SUCCESS]: (state, action) => ({ ...state, showProgressBar: false }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[GET_BALANCES_ERROR]: state => ({ ...state, error: true })
	},
	calendar.reducers,
	accrualReportCalendar.reducers,
	datepicker.reducers,
	filters.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.finReports.data,
	getCalendar: state => calendar.selectors.getCalendar(state => state.finReports.calendar, state => state)(state),
	getDatepicker: state => datepicker.selectors.getDatePicker(
		state => state.finReports.datepicker, state => state
	)(state),
	getAccrualReportCalendar: state => accrualReportCalendar.selectors.getCalendar(
		state => state.finReports.accrualReportCalendar, state => state
	)(state),
	getCurrentCurrencies: state => {
		const formCurrency = getFormValues('finReportCurrenciesSelectForm')(state);

		return _map(formCurrency && formCurrency.currency, curr => curr.value);
	},
	getListOfCurrencies: state => createSelectOptionsFromSysEnums(state.root.lists['available-currencies']),
	getIntl: state => intl(state),
	getProgressBar: state => state.finReports.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getFilters: state => filters.selectors.getFilters(
		state => state.finReports.filters,
		state => state
	)(state),
	getError: state => state.finReports.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/fin-reports/index.js